import React from "react";
import { UserCog } from "lucide-react";
import { Label } from "../../ui/label";
import { Input } from "../../ui/input";
import { FormData, FormErrors, sanitizeName, sanitizeEmail, sanitizePhone } from "../../../hooks/useCreateRecordForm";

interface Step2AsesorProps {
  formData: FormData;
  errors: FormErrors;
  updateFormData: <K extends keyof FormData>(key: K, value: FormData[K]) => void;
  validateField: (fieldName: keyof FormData) => boolean;
  isAnimating: boolean;
}

export const Step2Asesor: React.FC<Step2AsesorProps> = ({
  formData,
  errors,
  updateFormData,
  validateField,
  isAnimating,
}) => {
  return (
    <div
      className={`space-y-8 transition-all duration-500 ${isAnimating ? "opacity-0 translate-x-4" : "opacity-100 translate-x-0"}`}
    >
      <div className="space-y-6">
        <div className="flex items-center gap-3 pb-4">
          <UserCog className="w-5 h-5 text-gray-700" />
          <h3 className="text-lg font-semibold text-gray-900">
            Datos del Asesor
          </h3>
        </div>
        <p className="text-sm text-gray-500 -mt-4">
          Información de contacto del asesor asignado al expediente
        </p>
      </div>

      {/* Campos del formulario */}
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Nombre */}
          <div>
            <Label htmlFor="advisorName" className="text-sm font-medium text-gray-700 mb-2 block">
              Nombre *
            </Label>
            <Input
              id="advisorName"
              value={formData.advisorName}
              onChange={(e) => updateFormData("advisorName", sanitizeName(e.target.value))}
              onBlur={() => validateField("advisorName")}
              placeholder="Ingresa el nombre"
              aria-invalid={!!errors.advisorName}
              maxLength={50}
            />
            {errors.advisorName && (
              <p className="text-xs text-red-600 mt-1">{errors.advisorName}</p>
            )}
          </div>

          {/* Apellido paterno */}
          <div>
            <Label htmlFor="advisorFirstSurname" className="text-sm font-medium text-gray-700 mb-2 block">
              Apellido paterno *
            </Label>
            <Input
              id="advisorFirstSurname"
              value={formData.advisorFirstSurname}
              onChange={(e) => updateFormData("advisorFirstSurname", sanitizeName(e.target.value))}
              onBlur={() => validateField("advisorFirstSurname")}
              placeholder="Ingresa el apellido paterno"
              aria-invalid={!!errors.advisorFirstSurname}
              maxLength={50}
            />
            {errors.advisorFirstSurname && (
              <p className="text-xs text-red-600 mt-1">{errors.advisorFirstSurname}</p>
            )}
          </div>

          {/* Apellido materno */}
          <div>
            <Label htmlFor="advisorSecondSurname" className="text-sm font-medium text-gray-700 mb-2 block">
              Apellido materno
            </Label>
            <Input
              id="advisorSecondSurname"
              value={formData.advisorSecondSurname}
              onChange={(e) => updateFormData("advisorSecondSurname", sanitizeName(e.target.value))}
              onBlur={() => validateField("advisorSecondSurname")}
              placeholder="Ingresa el apellido materno"
              aria-invalid={!!errors.advisorSecondSurname}
              maxLength={50}
            />
            {errors.advisorSecondSurname && (
              <p className="text-xs text-red-600 mt-1">{errors.advisorSecondSurname}</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Correo */}
          <div className="lg:col-span-2">
            <Label htmlFor="advisorEmail" className="text-sm font-medium text-gray-700 mb-2 block">
              Correo electrónico *
            </Label>
            <Input
              id="advisorEmail"
              type="email"
              value={formData.advisorEmail}
              onChange={(e) => updateFormData("advisorEmail", sanitizeEmail(e.target.value))}
              onBlur={() => validateField("advisorEmail")}
              placeholder="correo@ejemplo.com"
              className="lowercase"
              aria-invalid={!!errors.advisorEmail}
              maxLength={100}
            />
            {errors.advisorEmail && (
              <p className="text-xs text-red-600 mt-1">{errors.advisorEmail}</p>
            )}
          </div>

          {/* Teléfono */}
          <div>
            <Label htmlFor="advisorPhoneNumber" className="text-sm font-medium text-gray-700 mb-2 block">
              Teléfono *
            </Label>
            <Input
              id="advisorPhoneNumber"
              type="tel"
              value={formData.advisorPhoneNumber}
              onChange={(e) => updateFormData("advisorPhoneNumber", sanitizePhone(e.target.value))}
              onBlur={() => validateField("advisorPhoneNumber")}
              placeholder="1234567890"
              maxLength={10}
              aria-invalid={!!errors.advisorPhoneNumber}
            />
            {errors.advisorPhoneNumber && (
              <p className="text-xs text-red-600 mt-1">{errors.advisorPhoneNumber}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
